/**
 * legacyRedirects.ts — Legacy URL Redirect Mapping
 *
 * Centralizes all backward-compatibility redirects in one place.
 * These map old/removed URLs to their current destinations.
 *
 * Usage in App.tsx:
 *   {LEGACY_REDIRECTS.map(r => (
 *     <Route key={r.from} path={r.from} element={<Navigate to={r.to} replace />} />
 *   ))}
 */

export interface LegacyRedirect {
  from: string;
  to: string;
}

export const LEGACY_REDIRECTS: LegacyRedirect[] = [
  // Business Hub sub-routes → unified Business Hub with tab params.
  // NOTE: /business/customers, /business/orders and /business/contacts are NOT
  // redirected — App.tsx declares real standalone pages for those paths first,
  // which always win over a same-path <Navigate> (entries here would be dead).
  { from: '/business/inventory', to: '/business?tab=inventory' },
  { from: '/business/marketing', to: '/business?tab=ads' },
  { from: '/business/finance', to: '/business?tab=finance' },
  { from: '/business/hub', to: '/business' },
  { from: '/business/logistics', to: '/business?tab=orders' },
  { from: '/business/operations', to: '/business/orders' },

  // Marketing legacy paths → Studio
  { from: '/marketing/email', to: '/studio' },
  { from: '/marketing/email-studio', to: '/studio' },
  { from: '/marketing/email-marketing', to: '/studio' },
  { from: '/marketing/autopilot', to: '/studio' },
  { from: '/marketing/canvas', to: '/studio' },
  { from: '/marketing/hub', to: '/studio' },

  // Flow builder → Automations
  { from: '/flow-builder-advanced', to: '/automations' },
  { from: '/flow-builder', to: '/automations' },
  { from: '/flow-builder/:id', to: '/automations' },

  // Settings legacy paths
  { from: '/settings/general', to: '/account' },
  { from: '/settings/team', to: '/settings/brand/team' },
  { from: '/settings/developers', to: '/account?tab=integrations' },
  // Brand DNA + Integrations were merged into the Account hub as tabs.
  { from: '/brand-dna', to: '/account?tab=brand' },
  { from: '/settings/integrations', to: '/account?tab=integrations' },
  { from: '/settings/security', to: '/settings/general' },
  { from: '/settings/privacy', to: '/settings/general' },

  // Dedup: 2026-05-31 audit found two pairs of duplicate routes serving
  // different subsets of the same data — confusing and lossy.
  //   LIVE-14: /inbox (20 conv) vs /inbox-view (0 conv). Keep /inbox.
  //   LIVE-15: /campaigns vs /studio?tab=campaigns. Keep the Studio tab so
  //            Flows/Templates/Campaigns are reached uniformly.
  { from: '/inbox-view', to: '/inbox' },
  { from: '/campaigns', to: '/studio?tab=campaigns' },

  // Top-level legacy paths → new locations
  { from: '/email-builder', to: '/studio' },
  { from: '/social', to: '/marketing/social' },
  { from: '/social-automator', to: '/marketing/social' },
  { from: '/marketing/popups', to: '/popups' },
  { from: '/ai-decisions', to: '/business' },
  { from: '/ai-planner', to: '/studio' },
  { from: '/content', to: '/business' },
  { from: '/inventory', to: '/business/inventory' },
  // Typed from memory by reviewers and merchants alike; landed on the hub
  // overview with no hint (Cowork run, 2026-09-26).
  { from: '/products', to: '/business/products' },
  { from: '/logistics', to: '/business/logistics' },
  { from: '/finance', to: '/business/finance' },
  { from: '/suppliers', to: '/business/suppliers' },
  { from: '/integrations', to: '/account?tab=integrations' },
  { from: '/settings', to: '/settings/general' },
  { from: '/customers', to: '/business/customers' },
  { from: '/commerce', to: '/business/contacts' },
  { from: '/marketing/seo', to: '/business' },
];
